// Port of Flutter's doorScreen.dart (DeviceID 3) — the door lock keypad.
//
// "Enter Passcode", four dots, then a 3-column numpad (1-9, blank, 0, ⌫). The
// PIN checked against is the LOGGED-IN USER'S PASSWORD — screenRedirector passes
// user_data[0]['Password'] in as `correctPin`. It is not a per-device code.
//
// A correct PIN publishes OPENDOOR and shows a blocking confirmation; a wrong
// one clears the dots and flashes "Incorrect PIN".
//
// The check is local, exactly as in the Dart. That is a client-side gate on a
// convenience action, not a security boundary — the device itself is what
// authorises the command.

import React, { useState } from 'react';
import {
  Box, Typography, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, useTheme,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { useNavigate } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { publishCommand } from '../../../../core/utils/deviceController';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '<'];

export default function DoorScreen({ device }) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { user, userId } = useApp();
  const uno = device?.UniqueNumber;
  const correctPin = String(user?.Password ?? '');

  const [pin, setPin] = useState('');
  const [wrong, setWrong] = useState(false);
  const [opened, setOpened] = useState(false);

  const press = (key) => {
    if (key === '<') { setPin((p) => p.slice(0, -1)); return; }
    if (pin.length >= 4) return;

    const next = pin + key;
    setPin(next);
    if (next.length < 4) return;

    if (next === correctPin) {
      publishCommand(uno, 'OPENDOOR', userId);
      setOpened(true);
    } else {
      setPin('');
      setWrong(true);
    }
  };

  const panelWidth = Math.min(window.innerWidth, 420);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} subtitle={device?.LocationName ?? ''} />

      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      }}
      >
        <Box sx={{ width: '100%', maxWidth: panelWidth, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 26, color: c.text, fontWeight: 500 }}>
            Enter Passcode
          </Typography>

          <Box sx={{ height: 30 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  mx: 1.25,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `1px solid ${c.text}`,
                  bgcolor: i < pin.length ? c.text : 'transparent',
                }}
              />
            ))}
          </Box>

          <Box sx={{ height: 50 }} />

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            p: 5,
          }}
          >
            {KEYS.map((key, i) => (key === '' ? (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`gap-${i}`} />
            ) : (
              <Box
                key={key}
                onClick={() => press(key)}
                sx={{
                  m: 1,
                  aspectRatio: '1',
                  borderRadius: '50%',
                  border: `1px solid ${c.text}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {key === '<' ? (
                  <BackspaceIcon sx={{ color: c.text }} />
                ) : (
                  <Typography sx={{ color: c.text, fontSize: 28, fontWeight: 500 }}>
                    {key}
                  </Typography>
                )}
              </Box>
            )))}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={wrong}
        autoHideDuration={1000}
        onClose={() => setWrong(false)}
        message="Incorrect PIN"
      />

      <Dialog
        open={opened}
        disableEscapeKeyDown
        PaperProps={{ sx: { bgcolor: c.back } }}
      >
        <DialogTitle sx={{ color: c.text }}>Door Unlocked</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: c.text }}>
            The door has been successfully opened.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setOpened(false); navigate(-1); }}
            sx={{ color: c.button }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
